import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Card, CardBody, Container } from "reactstrap";
import BreadCrumb from "components/common/BreadCrumb";
import PageLoader from "components/common/PageLoader";
import { getDocumentTitle } from "utils/helpers";
import { getPayrollRunById } from "features/Payroll/services/PayrollService";
import type { PayrollItem, PayrollRun } from "features/Payroll/types/payroll.types";
import PayrollRunDetailContext from "./PayrollRunDetailContext";
import Section from "./Section";

const EMPTY_PAYROLL_ITEMS: PayrollItem[] = [];

const PayrollRunOverview = () => {
  const { id } = useParams<{ id: string }>();
  const runId = Number(id);
  const [loading, setLoading] = useState(true);
  const [run, setRun] = useState<PayrollRun | null>(null);

  const refetch = useCallback(async () => {
    if (!Number.isFinite(runId) || runId <= 0) return;
    try {
      const res = await getPayrollRunById(runId);
      if (res.success && res.data) setRun(res.data);
      else {
        setRun(null);
        toast.error(res.message || "Payroll run not found");
      }
    } catch (err: unknown) {
      setRun(null);
      const msg = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      toast.error(msg || "Could not load payroll run");
    }
  }, [runId]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      await refetch();
      if (!cancelled) setLoading(false);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [refetch]);

  useEffect(() => {
    document.title = getDocumentTitle(
      run ? `Payroll run ${run.code ?? `#${run.id}`}` : "Payroll run",
    );
  }, [run]);

  const lineItems = run?.items ?? EMPTY_PAYROLL_ITEMS;
  const totals = useMemo(
    () => ({
      employees: lineItems.length,
      gross: Number(run?.totalGrossAmount ?? 0),
      net: Number(run?.totalNetAmount ?? 0),
    }),
    [lineItems.length, run?.totalGrossAmount, run?.totalNetAmount],
  );

  const breadcrumbTitle =
    run && !loading ? `Payroll run ${run.code ?? `#${run.id}`}` : "Payroll run";

  if (!Number.isFinite(runId) || runId <= 0) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Payroll run"
            pageTitle="Finance"
            backTo={{ label: "Payroll runs", path: "/finance/payroll" }}
          />
          <Card className="shadow-none border mt-3">
            <CardBody className="p-4 text-center">
              <p className="text-muted mb-3">
                This link doesn&apos;t point to a valid payroll run.
              </p>
              <Link to="/finance/payroll" className="btn btn-primary btn-sm">
                <i className="ri-arrow-left-line align-bottom me-1" />
                Back to payroll
              </Link>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={breadcrumbTitle}
          pageTitle="Finance"
          backTo={{ label: "Payroll runs", path: "/finance/payroll" }}
        />

        {loading ? (
          <PageLoader message="Loading payroll run…" />
        ) : !run ? (
          <Card className="shadow-none border">
            <CardBody className="p-4">
              <div className="text-center py-3">
                <div className="avatar-md mx-auto mb-3">
                  <span className="avatar-title rounded-circle bg-primary-subtle text-primary">
                    <i className="ri-error-warning-line fs-20" />
                  </span>
                </div>
                <h5 className="mb-2">We couldn&apos;t load this run</h5>
                <p className="text-muted mb-4 mx-auto" style={{ maxWidth: 420 }}>
                  Check your connection or try again. You can also return to the
                  payroll list and open another run.
                </p>
                <div className="hstack gap-2 justify-content-center flex-wrap">
                  <Button color="primary" onClick={() => void refetch()}>
                    <i className="ri-refresh-line align-bottom me-1" />
                    Try again
                  </Button>
                  <Link to="/finance/payroll" className="btn btn-soft-secondary">
                    <i className="ri-arrow-left-line align-bottom me-1" />
                    Back to payroll
                  </Link>
                </div>
              </div>
            </CardBody>
          </Card>
        ) : (
          <PayrollRunDetailContext.Provider value={{ run, refetch, lineItems, totals }}>
            <Section />
          </PayrollRunDetailContext.Provider>
        )}
      </Container>
    </div>
  );
};

export default PayrollRunOverview;
